import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMovies, deleteMovie } from "../redux/slices/moviesSlice";
import AddEditModal from "./AddEditModal";
import ImportMoviesModal from "./ImportMoviesModal";
import { Edit2, Trash2, Film, Plus, Upload } from "lucide-react";

const MoviesList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const dispatch = useDispatch();
  const movies = useSelector((state) => state.movies.movies);
  const loading = useSelector((state) => state.movies.loading);
  
  // Estados para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [moviesPerPage] = useState(10);
  
  // Cálculos para la paginación
  const indexOfLastMovie = currentPage * moviesPerPage;
  const indexOfFirstMovie = indexOfLastMovie - moviesPerPage;
  const currentMovies = Array.isArray(movies) ? movies.slice(indexOfFirstMovie, indexOfLastMovie) : [];

  useEffect(() => {
    dispatch(fetchMovies());
  }, [dispatch]);

  const handleOpenModal = (movie = null) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedMovie(null);
    setIsModalOpen(false);
  };

  const handleOpenImportModal = () => {
    console.log("Abriendo modal de importación CSV");
    setIsImportModalOpen(true);
    console.log("Estado actualizado isImportModalOpen:", true);
  };

  const handleCloseImportModal = () => {
    console.log("Cerrando modal de importación CSV");
    setIsImportModalOpen(false);
  };

  const handleDeleteMovie = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta película? Esta acción no se puede deshacer.')) {
      dispatch(deleteMovie(id));
    }
  };
  
  // Funciones para la paginación
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };
  
  const goToNextPage = () => {
    const totalPages = Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage);
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold bg-white bg-clip-text text-transparent">
          Gestión de Películas
        </h1>
        <div className="flex space-x-3">
          <button
            onClick={handleOpenImportModal}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            <Upload className="h-5 w-5" />
            Importar CSV
          </button>
          <button
            onClick={() => handleOpenModal()} 
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            <Plus className="h-5 w-5" />
            Agregar Película
          </button>
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Título
                </th>
                <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Géneros
                </th>
                {/* <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Estado
                </th> */}
                <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800/50 divide-y divide-gray-700">
              {Array.isArray(currentMovies) && currentMovies.length > 0 ? currentMovies.map((movie) => (
                <tr key={movie.id || `movie-${Math.random()}`} className="hover:bg-gray-700/50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{movie.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-2">
                      {movie.genre ? (
                        // Convertir el string de géneros a array si es necesario
                        (typeof movie.genre === 'string' ? movie.genre.split(", ") : 
                         Array.isArray(movie.genre) ? movie.genre : []).map((genre, index) => (
                          <span 
                            key={`${movie.id}-genre-${index}`}
                            className="px-2 py-1 text-xs font-medium rounded-full bg-blue-900/50 text-blue-400 border border-blue-400/30"
                          >
                            {genre}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400">Sin géneros</span>
                      )}
                    </div>
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border
                      ${movie.active 
                        ? 'bg-green-900/50 text-green-400 border-green-400/30' 
                        : 'bg-gray-700 text-gray-400 border-gray-500/30'}`}>
                      {movie.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleOpenModal(movie)}
                        className="text-blue-400 hover:text-blue-300 transition-colors duration-200 flex items-center gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        <span>Editar</span>
                      </button>
                      <button 
                        onClick={() => handleDeleteMovie(movie.id)}
                        className="text-red-400 hover:text-red-300 transition-colors duration-200 flex items-center gap-2"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-center text-gray-400">
                    No hay películas disponibles
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      {Array.isArray(movies) && movies.length > 0 && (
        <div className="flex justify-between items-center mt-6 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md 
                ${currentPage === 1 
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
            >
              Anterior
            </button>
            <button
              onClick={goToNextPage}
              disabled={currentPage === Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage)}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium rounded-md 
                ${currentPage === Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage) 
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-400">
                Mostrando <span className="font-medium">{indexOfFirstMovie + 1}</span> a <span className="font-medium">
                  {Math.min(indexOfLastMovie, Array.isArray(movies) ? movies.length : 0)}
                </span> de <span className="font-medium">{Array.isArray(movies) ? movies.length : 0}</span> películas
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 text-sm font-medium 
                    ${currentPage === 1 
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                      : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Números de página */}
                {Array.from({ length: Math.min(5, Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage)) }).map((_, index) => {
                  // Cálculo para mostrar las páginas correctas cuando hay muchas
                  let pageNumber;
                  const totalPages = Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage);
                  
                  if (totalPages <= 5) {
                    // Si hay 5 o menos páginas, mostrar todas
                    pageNumber = index + 1;
                  } else {
                    // Si hay más de 5 páginas, mostrar de forma inteligente
                    if (currentPage <= 3) {
                      pageNumber = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + index;
                    } else {
                      pageNumber = currentPage - 2 + index;
                    }
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium
                        ${currentPage === pageNumber
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage)}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 text-sm font-medium 
                    ${currentPage === Math.ceil((Array.isArray(movies) ? movies.length : 0) / moviesPerPage) 
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                      : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
                >
                  <span className="sr-only">Siguiente</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      <AddEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        data={selectedMovie}
        type="movie"
      />
      
      <ImportMoviesModal
        isOpen={isImportModalOpen}
        onClose={handleCloseImportModal}
      />
    </div>
  );
};

export default MoviesList;